import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CHURCH_IMAGES } from '../data/mock';
import { PageHeader } from './MassTimes';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { AddButton, CardControls } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import EditableText from '../components/EditableText';

const TEAM_FIELDS = [
  { name: 'name', label: 'Name', required: true, placeholder: 'Fr. Sebastian Michael' },
  { name: 'role', label: 'Role / Title', required: true, placeholder: 'Pastor' },
  { name: 'type', label: 'Type', type: 'select', required: true, options: [
    { value: 'clergy', label: 'Clergy' },
    { value: 'staff', label: 'Staff' },
  ]},
  { name: 'bio', label: 'Biography (clergy only)', type: 'textarea', rows: 4, placeholder: 'Short biography...' },
];

const initials = (name) => name.split(' ').map(n => n[0]).slice(0, 2).join('');

const About = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const { data } = await api.listTeam();
    setTeam(data);
  };
  useEffect(() => { load(); }, []);

  const clergy = team.filter(t => t.type === 'clergy');
  const staff = team.filter(t => t.type === 'staff');

  const handleSave = async (values) => {
    const payload = { ...values, bio: values.bio || '' };
    if (editing?.mode === 'new') {
      const { data } = await api.createTeam(payload);
      setTeam([...team, data]);
      toast({ title: 'Team member added' });
    } else {
      const { data } = await api.updateTeam(editing.id, payload);
      setTeam(team.map(x => x.id === data.id ? data : x));
      toast({ title: 'Team member updated' });
    }
  };

  const handleDelete = async () => {
    await api.deleteTeam(deleteTarget.id);
    setTeam(team.filter(x => x.id !== deleteTarget.id));
    toast({ title: 'Team member deleted' });
    setDeleteTarget(null);
  };

  return (
    <>
      <PageHeader
        eyebrow="Our Story"
        title="About All Saints"
        subtitle="A faithful community of disciples serving Ladysmith and Chemainus — rooted in the sacraments, formed by Scripture, and sent to serve."
      />

      {/* Our Story */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-3">Our Parish</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45] mb-6 leading-tight">One Faith, Two Historic Churches</h2>
            <EditableText
              contentKey="about_story_body"
              label="About: Our Story"
              rows={12}
              defaultValue={`All Saints Catholic Parish was formed by the union of two historic Catholic communities on Vancouver Island — St. Mary's in Ladysmith and St. Joseph's in Chemainus — each with a rich history of faith spanning generations of Coast Salish and settler families.

Together, we are one parish under the pastoral care of the Archdiocese of Vancouver, sharing one pastor, one mission, and one liturgical calendar while honoring the distinct traditions of each worship community.

We are a faithful community of disciples — called to worship God, form intentional disciples of Jesus Christ, and serve the people of Vancouver Island.`}
              paragraphClass="text-[#3f3a34] leading-relaxed"
            />
          </div>
          <div>
            <img src={CHURCH_IMAGES.interior} alt="Church interior" className="w-full h-[500px] object-cover" />
          </div>
        </div>
      </section>

      {/* Clergy */}
      <section className="bg-[#f2ead6]/40 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-3">Clergy</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45]">Our Pastors & Deacons</h2>
            {isAdmin && (
              <div className="mt-4">
                <AddButton onClick={() => setEditing({ _initType: 'clergy', mode: 'new' })} label="Add Clergy" />
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {clergy.map(c => (
              <div key={c.id} className="relative group bg-white border border-[#e9e0cc] p-8">
                {isAdmin && <CardControls onEdit={() => setEditing(c)} onDelete={() => setDeleteTarget(c)} />}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-full bg-[#3a2359] text-[#e6d4a8] flex items-center justify-center font-display text-2xl">
                    {initials(c.name)}
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-[#2a1a45] leading-tight">{c.name}</h3>
                    <p className="text-sm text-[#a68a5b] tracking-wide">{c.role}</p>
                  </div>
                </div>
                <p className="text-sm text-[#5a534a] leading-relaxed">{c.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parish Staff */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-3">Parish Team</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2a1a45]">Staff</h2>
          {isAdmin && (
            <div className="mt-4">
              <AddButton onClick={() => setEditing({ _initType: 'staff', mode: 'new' })} label="Add Staff" />
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {staff.map(s => (
            <div key={s.id} className="relative group bg-white border border-[#e9e0cc] p-6 text-center">
              {isAdmin && <CardControls onEdit={() => setEditing(s)} onDelete={() => setDeleteTarget(s)} />}
              <div className="w-14 h-14 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center font-display text-xl mx-auto mb-3">
                {initials(s.name)}
              </div>
              <h3 className="font-display text-lg text-[#2a1a45] leading-tight">{s.name}</h3>
              <p className="text-xs text-[#78716c] mt-1">{s.role}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link to="/new-parishioner" className="inline-block px-7 py-3 bg-[#3a2359] text-white text-sm tracking-wide hover:bg-[#4d2f75]">
            Register as a New Parishioner
          </Link>
        </div>
      </section>

      <EditDialog
        open={!!editing}
        title={editing?.mode === 'new' ? 'Add Team Member' : 'Edit Team Member'}
        fields={TEAM_FIELDS}
        initial={editing?.mode === 'new'
          ? { name: '', role: '', type: editing._initType || 'staff', bio: '' }
          : (editing || {})}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this team member?"
        message={deleteTarget ? `“${deleteTarget.name}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default About;
